import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";

function UserKyc({ isTab = false }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [kycData, setKycData] = useState(null);

  // Form fields matching backend Kyc.js model exactly
  const [formData, setFormData] = useState({
    full_name: "",
    mobile_number: "",
    email: "",
    gender: "",
    dob: "",
    photo: "",
    aadhar_frant_image: "",
    aadhar_back_image: "",
    pancard_image: "",
    nagrita_frant_photo: "",
    nagrita_back_photo: "",
    bank_name: "",
    account_number: "",
    ifsc: "",
    bank_branch: "",
    cancel_cheque: "",
  });

  const [uploadingField, setUploadingField] = useState("");

  const isApproved = Boolean(
    kycData && kycData.status && kycData.status.toLowerCase() === "approved"
  );

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/user/loggedinuser");
      const { status, user: fetchedUser, kyc: fetchedKyc } = response.data;
      if (status) {
        setUser(fetchedUser);
        if (fetchedKyc) {
          setKycData(fetchedKyc);
          setFormData({
            full_name: fetchedKyc.full_name || fetchedUser.name || "",
            mobile_number: fetchedKyc.mobile_number || fetchedUser.mobile || "",
            email: fetchedKyc.email || fetchedUser.email || "",
            gender: fetchedKyc.gender || "",
            dob: fetchedKyc.dob || "",
            photo: fetchedKyc.photo || "",
            aadhar_frant_image: fetchedKyc.aadhar_frant_image || "",
            aadhar_back_image: fetchedKyc.aadhar_back_image || "",
            pancard_image: fetchedKyc.pancard_image || "",
            nagrita_frant_photo: fetchedKyc.nagrita_frant_photo || "",
            nagrita_back_photo: fetchedKyc.nagrita_back_photo || "",
            bank_name: fetchedKyc.bank_name || "",
            account_number: fetchedKyc.account_number || "",
            ifsc: fetchedKyc.ifsc || "",
            bank_branch: fetchedKyc.bank_branch || "",
            cancel_cheque: fetchedKyc.cancel_cheque || "",
          });
        } else if (fetchedUser) {
          setFormData((prev) => ({
            ...prev,
            full_name: fetchedUser.name || "",
            mobile_number: fetchedUser.mobile || "",
            email: fetchedUser.email || "",
          }));
        }
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleInputChange = (e) => {
    if (isApproved) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e, fieldName, fileTypeKey = "photo") => {
    if (isApproved) {
      toast.info("Approved KYC details cannot be modified.");
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingField(fieldName);
      let uploadedFileName = "";

      // Attempt 1: Upload via /user/v4/upload/kyc/file with single field "file"
      try {
        const formData1 = new FormData();
        formData1.append("file", file);
        formData1.append("type", `(${fileTypeKey})`);

        const response = await axios.post("/user/v4/upload/kyc/file", formData1, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data && response.data.filePath) {
          uploadedFileName = response.data.filePath;
        }
      } catch (err) {
        // Fallback Attempt 2: Upload via /uploadFiles with single field "image"
        const formData2 = new FormData();
        formData2.append("image", file);
        formData2.append("field", fieldName);
        formData2.append("by", "kyc");
        if (user?._id) {
          formData2.append("id", user._id);
        }

        const response = await axios.post("/uploadFiles", formData2, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data && response.data.image) {
          uploadedFileName = response.data.image;
        }
      }

      if (uploadedFileName) {
        setFormData((prev) => ({ ...prev, [fieldName]: uploadedFileName }));
        toast.success("Image uploaded successfully!");
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "File upload error");
    } finally {
      setUploadingField("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isApproved) {
      toast.warning("Approved KYC cannot be updated.");
      return;
    }
    try {
      setSubmitting(true);
      let endpoint = "/user/v4/kyc";
      let payload = { ...formData };

      if (kycData && kycData._id) {
        endpoint = "/user/v4/kyc/update";
        payload.kycId = kycData._id;
      }

      const response = await axios.post(endpoint, payload);
      const { status, message } = response.data;
      if (status) {
        toast.success(message || "KYC Submitted Successfully!");
        fetchUserData();
      } else {
        toast.error(message || "Submission failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred while submitting KYC.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderDocumentCard = (fieldName, label, fileTypeKey) => {
    const imagePath = formData[fieldName];
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    let src = imagePath;
    if (imagePath && !imagePath.startsWith("http://") && !imagePath.startsWith("https://") && !imagePath.startsWith("data:")) {
      src = `${backendUrl}/uploads/idproof/${imagePath}`;
    }

    return (
      <div className="col-md-4 mb-3">
        <div className="card h-100 border shadow-sm">
          <div className="card-body d-flex flex-column justify-content-between p-3">
            <div>
              <label className="form-label font-weight-bold text-dark mb-2">{label}</label>
              {!isApproved && (
                <div className="mb-2">
                  <input
                    type="file"
                    className="form-control form-control-sm"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, fieldName, fileTypeKey)}
                  />
                </div>
              )}
              {uploadingField === fieldName && (
                <div className="text-primary small mb-2">
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Uploading image...
                </div>
              )}
            </div>

            {imagePath ? (
              <div className="text-center p-2 border rounded bg-light mt-2">
                <a href={src} target="_blank" rel="noopener noreferrer" title="Click to view full image">
                  <img
                    src={src}
                    alt={label}
                    style={{ maxHeight: "140px", maxWidth: "100%", objectFit: "contain", borderRadius: "4px" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = "none";
                    }}
                  />
                </a>
                <div className="mt-1">
                  <a href={src} target="_blank" rel="noopener noreferrer" className="badge bg-info text-decoration-none">
                    <i className="ti-eye me-1"></i>View Image
                  </a>
                </div>
              </div>
            ) : (
              <div className="text-center p-3 border rounded bg-light text-muted mt-2">
                <i className="ti-image fs-1 d-block mb-1 opacity-50"></i>
                <small>No document uploaded</small>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStatusBadge = () => {
    if (!kycData) {
      return (
        <div className="alert alert-warning d-flex align-items-center mb-4" role="alert">
          <i className="ti-info-alt me-2 fs-4"></i>
          <div>
            <strong>KYC Not Submitted:</strong> Please complete your personal information, document uploads, and bank details to verify your account.
          </div>
        </div>
      );
    }

    const status = kycData.status ? kycData.status.toLowerCase() : "pending";
    if (status === "approved") {
      return (
        <div className="alert alert-success d-flex align-items-center mb-4" role="alert">
          <i className="ti-check-box me-2 fs-4"></i>
          <div>
            <strong>KYC Approved:</strong> Your KYC verification is complete and verified by admin. You can view all your approved details below.
          </div>
        </div>
      );
    } else if (status === "rejected") {
      return (
        <div className="alert alert-danger mb-4" role="alert">
          <div className="d-flex align-items-center">
            <i className="ti-close me-2 fs-4"></i>
            <div>
              <strong>KYC Rejected:</strong> Your KYC was rejected by admin.
            </div>
          </div>
          {kycData.comment && (
            <div className="mt-2 pt-2 border-top">
              <strong>Reason:</strong> {kycData.comment}
            </div>
          )}
          <div className="mt-2 text-muted">
            Please update the incorrect details or documents below and resubmit for approval.
          </div>
        </div>
      );
    } else {
      return (
        <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
          <i className="ti-time me-2 fs-4"></i>
          <div>
            <strong>KYC Pending Verification:</strong> Your KYC details have been submitted and are under review by admin.
          </div>
        </div>
      );
    }
  };

  const content = (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="card-title mb-0">KYC Verification</h4>
          {kycData && kycData.status && (
            <span
              className={`badge bg-${
                kycData.status.toLowerCase() === "approved"
                  ? "success"
                  : kycData.status.toLowerCase() === "rejected"
                  ? "danger"
                  : "warning"
              } text-uppercase px-3 py-2`}
              style={{ fontSize: "0.85rem" }}
            >
              {kycData.status}
            </span>
          )}
        </div>

        {renderStatusBadge()}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <Tabs defaultActiveKey="personal_info" id="kyc-tabs" className="mb-4">
              {/* Tab 1: Personal Details */}
              <Tab eventKey="personal_info" title="Personal Info">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      readOnly={isApproved}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Mobile Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleInputChange}
                      readOnly={isApproved}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      readOnly={isApproved}
                      required
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Gender</label>
                    {isApproved ? (
                      <input
                        type="text"
                        className="form-control"
                        value={formData.gender || "-"}
                        readOnly
                      />
                    ) : (
                      <select
                        className="form-select form-control"
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Date of Birth</label>
                    <input
                      type={isApproved ? "text" : "date"}
                      className="form-control"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      readOnly={isApproved}
                    />
                  </div>
                </div>
              </Tab>

              {/* Tab 2: Identity Documents */}
              <Tab eventKey="identity_docs" title="Identity Documents">
                <div className="row">
                  {renderDocumentCard("photo", "Profile Photo", "photo")}
                  {renderDocumentCard("aadhar_frant_image", "Aadhar Front Image", "aadhar_frant")}
                  {renderDocumentCard("aadhar_back_image", "Aadhar Back Image", "aadhar_back")}
                  {renderDocumentCard("pancard_image", "PAN Card Image", "pancart")}
                  {renderDocumentCard("nagrita_frant_photo", "Nagrita Front Photo", "nfr")}
                  {renderDocumentCard("nagrita_back_photo", "Nagrita Back Photo", "nback")}
                </div>
              </Tab>

              {/* Tab 3: Bank Details */}
              <Tab eventKey="bank_details" title="Bank Details">
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label">Bank Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleInputChange}
                      readOnly={isApproved}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Account Number</label>
                    <input
                      type="text"
                      className="form-control"
                      name="account_number"
                      value={formData.account_number}
                      onChange={handleInputChange}
                      readOnly={isApproved}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">IFSC Code</label>
                    <input
                      type="text"
                      className="form-control"
                      name="ifsc"
                      value={formData.ifsc}
                      onChange={handleInputChange}
                      readOnly={isApproved}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Bank Branch</label>
                    <input
                      type="text"
                      className="form-control"
                      name="bank_branch"
                      value={formData.bank_branch}
                      onChange={handleInputChange}
                      readOnly={isApproved}
                    />
                  </div>
                </div>

                <div className="row">
                  {renderDocumentCard("cancel_cheque", "Cancel Cheque / Passbook Copy", "cancel_cheque")}
                </div>
              </Tab>
            </Tabs>

            <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
              {isApproved ? (
                <button type="button" className="btn btn-success px-4" disabled>
                  <i className="ti-check-box me-2"></i>KYC Approved & Verified
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Submitting...
                    </>
                  ) : kycData && kycData._id ? (
                    "Update KYC Details"
                  ) : (
                    "Submit KYC"
                  )}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );

  if (isTab) {
    return content;
  }

  return (
    <div className="content-wrapper">
      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">{content}</div>
      </div>
    </div>
  );
}

export default UserKyc;
